import { FontIcon } from "@fluentui/react";
import { Tooltip } from "@fluentui/react-components";
import React, { useCallback, useState, useEffect } from "react";
import { useAppRouteParams } from "../../../AppRoutes";
import {
  SaveType,
  postFileVersionDataExcel,
  postFileVersionDataJSON,
} from "../../../api/files";
import { getAPIErrorDetailOrMesssage } from "../../../api/helpers";
import { DragAndDropUploadData } from "../../DragAndDropFields";
import styles from "./index.module.css";

/**
 * @notice
 * このフックは、JSONとExcelのアップロード処理を共通化するために作成されています。
 * 将来、アップロードタイプごとに大きく処理が変わる場合は、以下の手順でコンポジションを解除できます:
 * 1. 各タイプ固有のフックファイル（useUploadSheetDataJSON.tsx など）に直接ロジックをコピーする
 * 2. このファイルの実装を参考にしながら、タイプごとに必要な処理を追加/変更する
 * 3. 各フックファイルからこのファイルへの依存を削除する
 * 4. 必要に応じてこのファイルを削除する
 */

type WriteStatus = "waiting" | "in_progress" | "success" | "failed";
const statusMap: Record<
  WriteStatus,
  { className: string; message: string; iconName: string }
> = {
  waiting: {
    className: styles.waiting,
    message: "未実行",
    iconName: "CirclePause",
  },
  in_progress: {
    className: styles.processing,
    message: "実行中",
    iconName: "Sync",
  },
  success: {
    className: styles.success,
    message: "成功",
    iconName: "Completed",
  },
  failed: {
    className: styles.failed,
    message: "失敗",
    iconName: "ErrorBadge",
  },
};

type UploadFunction =
  | typeof postFileVersionDataExcel
  | typeof postFileVersionDataJSON;

/**
 * @description データアップロード用の共通ロジック
 * @param uploadedFileList アップロードされたファイルリスト
 * @param setUploadedFileList ファイルリスト更新関数
 * @param saveType 保存タイプ
 * @param uploadFunction ファイルタイプ別のアップロード関数
 */
export const useUploadSheetData = (
  uploadedFileList: DragAndDropUploadData[],
  setUploadedFileList: React.Dispatch<
    React.SetStateAction<DragAndDropUploadData[]>
  >,
  saveType: SaveType,
  uploadFunction: UploadFunction
) => {
  const { organizationId, versionId } = useAppRouteParams();

  const [status, setStatus] = useState<WriteStatus>("waiting");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (uploadedFileList.length === 0) {
      setStatus("waiting");
    }
  }, [uploadedFileList]);

  // APIによる生成実行可否
  const isEnableGenerateSheetData = uploadedFileList.length === 1;

  const runUpload = useCallback(async () => {
    setStatus("in_progress");
    try {
      const file = uploadedFileList[0].file;
      await uploadFunction({
        organizationId,
        versionId,
        file,
        saveType,
      });
      setStatus("success");
    } catch (error) {
      const { errorMessage } = getAPIErrorDetailOrMesssage(error);
      setErrorMessage(errorMessage);
      setStatus("failed");
    }
  }, [uploadedFileList, saveType, uploadFunction]);

  const getExtElement = useCallback(() => {
    const { className, message, iconName } = statusMap[status];
    return (
      <div className={styles.info}>
        {status === "failed" ? (
          <div className={className}>
            <div className={styles.status}>
              <FontIcon iconName={iconName} />
              <Tooltip content={errorMessage || ""} relationship="description">
                <div className={styles.hoverable}>{message}</div>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className={className}>
            <div className={styles.status}>
              <FontIcon iconName={iconName} />
              <div>{`${message}`}</div>
            </div>
          </div>
        )}
      </div>
    );
  }, [status]);

  const isDisablePrimaryButton =
    !isEnableGenerateSheetData || status !== "waiting";

  const isDisableCancelButton = status === "in_progress";

  return {
    runUpload,
    isDisablePrimaryButton,
    isDisableCancelButton,
    getExtElement,
  };
};
